<script lang="ts" setup>
  import { useTitropEvents } from '@/composables/useTitropEvents.ts'
  import Icon from '@/elements/Icon.vue'
  import type Inplace from 'primevue/inplace'
  import { isEmpty, isEqual } from 'lodash-es'
  import { whenTitropReady } from '@/composables/useTitrop.ts'

  const visibleRight = ref(false)

  interface NoticeTab {
    title: string
    content: string
    value: string
  }

  useTitropEvents().hotkeyToggleNoticesSidebar.on(() => (visibleRight.value = !visibleRight.value))
  //
  // const tabs = ref<{ title: string; content: string; value: string }[]>([
  //   { title: 'Tab 1', content: 'Tab 1 Content', value: '0' },
  //   { title: 'Tab 2', content: 'Tab 2 Content', value: '1' },
  //   { title: 'Tab 3', content: 'Tab 3 Content', value: '2' }
  // ])

  const tabs = ref<NoticeTab[]>([])
  const tabsNotModified = ref<NoticeTab[]>([])
  const numExistingTabs = ref<number>()
  const dataReady = ref(false)
  onMounted(() => {
    whenTitropReady(() => {
      numExistingTabs.value = useTitrop().value.config.user('notice_tabs_count', 0).value
      for (let tabIdx = 0; tabIdx < numExistingTabs.value; tabIdx++) {
        const pulledNoticeTab = useTitrop().value.config.user<NoticeTab>(`notice_tab_${tabIdx}`, {}).value
        if (!isEmpty(pulledNoticeTab)) tabs.value.push(pulledNoticeTab)
      }
      tabsNotModified.value = JSON.parse(JSON.stringify(tabs.value))
      dataReady.value = true
    })
  })

  const newTabName = ref('')

  function addNewTab(closeCallback: () => void) {
    tabs.value.push({ title: newTabName.value, content: '', value: String(tabs.value.length + 1) })
    newTabName.value = ''
    closeCallback()
  }

  const newTabInplace = ref<InstanceType<typeof Inplace>>()

  watchDebounced(
    tabs,
    (newVal) => {
      for (let tabIdx = 0; tabIdx < newVal.length; tabIdx++) {
        if (!isEqual(newVal[tabIdx], tabsNotModified.value[tabIdx])) {
          useTitrop().value.config.userSet<NoticeTab>(`notice_tab_${tabIdx}`, newVal[tabIdx])
        }
      }
      if (newVal.length != tabsNotModified.value.length) useTitrop().value.config.user('notice_tabs_count', 0).value = newVal.length

      tabsNotModified.value = JSON.parse(JSON.stringify(tabs.value))
    },
    { deep: true, debounce: 2000, maxWait: 10000 }
  )
</script>

<template>
  <Dialog
    v-if="dataReady"
    v-model:visible="visibleRight"
    :block-scroll="false"
    :modal="false"
    :close-on-escape="false"
    :dismissable="false"
    :dismissable-mask="false"
    :pt:header:class="'p-0 px-4 pt-1'"
    header="Notizen"
    class="min-w-[25vw] w-[25vw] max-w-[25vw] min-h-[33vh] bg-amber-200"
    draggable
    keep-in-viewport
    maximizable
    position="right">
    <Tabs
      :value="tabs[0]?.title"
      lazy
      scrollable>
      <TabList :pt:tablist:class="'bg-transparent gap-x-1'">
        <Tab
          v-for="tab in tabs"
          :key="tab.title"
          v-slot="slotProps"
          :value="tab.title"
          as-child>
          <div
            :class="[slotProps.class, 'flex items-center bg-amber-100 !border border-gray-300 rounded-sm p-0 py-0 px-1.5']"
            v-bind="slotProps.a11yAttrs"
            @click="slotProps.onClick">
            <span class="font-bold whitespace-nowrap">{{ tab.title }}</span>
          </div>
        </Tab>
        <Tab
          v-slot="slotProps"
          as-child
          value="__NEW__">
          <Inplace
            ref="newTabInplace"
            :pt:context:class="'p-0 flex items-center leading-base'"
            :pt:display:class="'p-0'">
            <template #display>
              <Icon
                class="cursor-pointer"
                icon="AddBoxIcon" />
            </template>
            <template #content="{ closeCallback }">
              <InputText
                v-model="newTabName"
                class="h-6 inline relative" />
              <Icon
                class="cursor-pointer inline relative place-self-center size-6 pt-1"
                icon="SaveIcon"
                @click="addNewTab(closeCallback)" />
            </template>
          </Inplace>
        </Tab>
      </TabList>

      <TabPanels>
        <TabPanel
          v-for="tab in tabs"
          :key="tab.title"
          :value="tab.title">
          <textarea
            v-model="tab.content"
            class="w-full h-full"></textarea>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </Dialog>
</template>

<style scoped>
  .p-tab-active {
  }

  .p-tabpanels {
    @apply p-0.5;
  }

  .p-tabpanels textarea {
    @apply rounded-md border-gray-200;
  }
</style>
